// helper functions

import moment from 'moment';
import { isSafari } from 'scribe-web-shared/functions';

import { CursiveRecorder, isElectron } from './electron';
import { openInNewTab } from './urls';

// Global standardize date formatting by replacing 'a' with '1'
moment.updateLocale('en', {
  relativeTime: {
    past: (diff) => (diff === 'just now' ? diff : `${diff} ago`),
    s: '1 second',
    m: '1 minute',
    h: '1 hour',
    d: '1 day',
    w: '1 week',
    M: '1 month',
    y: '1 year'
  }
});

export const isEmailValid = (email) => {
  const reg =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return reg.test(String(email).trim());
};

export const isSlugValid = (slug) => {
  const reg = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  return reg.test(String(slug));
};

export const isURLValid = (url, requireHttps = false) => {
  // test string matches
  // https://www.google.com
  // http://www.google.com
  // www.google.com
  // google.com
  if (requireHttps) {
    if (!url.startsWith('https://')) {
      return false;
    }
  }
  const reg =
    /^((https?:\/\/(?:www\.|(?!www)))?[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/;

  return reg.test(String(url));
};

export const getUrlVars = (url) => {
  const vars = {};

  url.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
    vars[key] = value;
  });
  return vars;
};

export const getUrlParam = (url, parameter) => {
  let value = '';

  if (url.indexOf(parameter) > -1) {
    value = getUrlVars(url)[parameter];
  }
  return value;
};

export const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);

export const openCustomLogoURL = (logoURL) => {
  if (!logoURL) return console.error('logoURL is undefined.');
  const url = logoURL.match(/^http[s]?:\/\//) ? logoURL : `https://${logoURL}`;

  if (isElectron()) {
    return CursiveRecorder().openURLInBrowser(url);
  }
  return openInNewTab(url);
};

export const isScribeDocumentStale = (scribe) => {
  const today = moment();
  const reminderInterval = scribe?.organization_owner?.update_reminder_interval;
  const expiresAtDate =
    scribe?.update_reminder_updated_at && reminderInterval
      ? moment(scribe?.update_reminder_updated_at).add(reminderInterval, 'days')
      : null;

  if (expiresAtDate && reminderInterval) {
    return today.isSameOrAfter(expiresAtDate, 'days');
  }
  return false;
};

// period between expiration date and two weeks before
export const isScribeDocumentNearingExpirationDate = (scribe) => {
  const today = moment();
  const reminderInterval = scribe?.organization_owner?.update_reminder_interval;
  const expiresAtDate =
    scribe?.update_reminder_updated_at && reminderInterval
      ? moment(scribe?.update_reminder_updated_at).add(reminderInterval, 'days')
      : null;

  if (expiresAtDate && reminderInterval) {
    const twoWeeksBefore = moment(expiresAtDate).subtract(2, 'weeks');

    return today.isBetween(twoWeeksBefore, expiresAtDate);
  }
  return false;
};

export const getScribeExpiryDate = (scribe) => {
  const reminderInterval = scribe?.organization_owner?.update_reminder_interval;
  const expiresAtDate =
    scribe?.update_reminder_updated_at && reminderInterval
      ? moment(scribe?.update_reminder_updated_at).add(reminderInterval, 'days').startOf('day').format('DD MMM YYYY')
      : null;

  return expiresAtDate;
};

export const scribeExpireInDays = (reminderDate, interval) => {
  const expiryDate = moment(reminderDate).add(interval, 'days').startOf('day');

  return expiryDate.diff(moment().startOf('day'), 'days');
};

export const expiryDays = (scribeFile) => {
  const days = scribeExpireInDays(getScribeExpiryDate(scribeFile), scribeFile.update_reminder_interval);
  const absDays = Math.abs(days);

  if (days === 0) {
    return 'Today';
  }
  return `${days > 0 ? 'In ' : ''}${absDays} day${absDays !== 1 ? 's' : ''}${days < 0 ? ' ago' : ''}`;
};

export const dateDiffInDays = (a, b) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / MS_PER_DAY);
};

const Plan = {
  ENTERPRISE: 'enterprise',
  PRO: 'pro',
  FREE: 'free',
  BASE: 'base',
  CORE: 'core',
  HUB: 'hub',
  GRID: 'grid'
};

const EnterprisePlans = [Plan.ENTERPRISE, Plan.BASE, Plan.GRID, Plan.CORE, Plan.HUB];

export const isSuperOrgPlanFullEnterprise = (superOrg) =>
  superOrg?.plan?.name === Plan.ENTERPRISE && superOrg?.enterprise_plan_type === 'enterprise';
export const isSuperOrgPlanEnterprise = (superOrg) => EnterprisePlans.includes(superOrg?.plan?.name);
export const isSuperOrgPlanPro = (superOrg) => superOrg?.plan?.name === Plan.PRO || superOrg?.stripe_subscription_status === 'active';
export const isSuperOrgPlanProTeam = (superOrg) =>
  superOrg?.plan?.name === Plan.PRO && superOrg?.stripe_subscription_status === 'active' && superOrg?.pro_plan_type === 'five_user_team';
export const isSuperOrgPlanProWithActiveSub = (superOrg) =>
  superOrg?.plan?.name === Plan.PRO && superOrg?.stripe_subscription_status === 'active';

/**
 * @description Check if super org plan is free
 * @param {*} superOrg
 * @returns boolean
 */
export const isSuperOrgPlanFree = (superOrg) => superOrg?.plan?.name === Plan.FREE;
export const isSuperOrgPlanBusiness = (superOrg) =>
  superOrg?.plan?.name === Plan.ENTERPRISE && superOrg?.enterprise_plan_type === 'self_serve';
export const isSuperOrgPlanBase = (superOrg) => superOrg?.plan?.name === Plan.BASE;
export const isSuperOrgPlanCore = (superOrg) => superOrg?.plan?.name === Plan.CORE;
export const isSuperOrgPlanHub = (superOrg) => superOrg?.plan?.name === Plan.HUB;
export const isSuperOrgOnlyGrid = (superOrg) => superOrg?.plan?.name === Plan.GRID;
// For isSuperOrgPlanGrid, we are checking for legacy Enterprise plan as well because it should incude everything that is available in Grid:
export const isSuperOrgPlanGrid = (superOrg) =>
  superOrg?.plan?.name === Plan.GRID || (superOrg?.plan?.name === Plan.ENTERPRISE && superOrg?.enterprise_plan_type !== 'self_serve');
export const isSuperOrgNonEnterprise = (superOrg) =>
  ![Plan.BASE, Plan.CORE, Plan.HUB, Plan.GRID, Plan.ENTERPRISE].includes(superOrg?.plan?.name);
export const isSuperOrgPlanEnterpriseModule = (superOrg) =>
  [Plan.BASE, Plan.CORE, Plan.HUB, Plan.GRID, Plan.ENTERPRISE].includes(superOrg?.plan?.name);
export const isAccountFree = (account) => isSuperOrgPlanFree(account?.active_organization?.super_organization);
export const nFormatter = (n, decimals = 1) => {
  let d = decimals;

  if (n.toString().length > 5) d = 0;
  if (n < 1e3) return n;
  if (n >= 1e3 && n < 1e6) return `${+(n / 1e3).toFixed(d)}K`;
  if (n >= 1e6 && n < 1e9) return `${+(n / 1e6).toFixed(d)}M`;
  if (n >= 1e9 && n < 1e12) return `${+(n / 1e9).toFixed(d)}B`;
};

export const formattedNum = (num) => {
  if (typeof navigator === 'undefined') {
    return nFormatter(num || 0);
  }
  return isSafari
    ? nFormatter(num || 0)
    : Intl.NumberFormat([navigator.language, 'en-US'], { notation: 'compact', compactDisplay: 'short' }).format(num || 0);
};

export const bytesToSize = (bytes) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  if (bytes === 0) return 'n/a';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);

  if (i === 0) return `${bytes} ${sizes[i]})`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

export const base64ArrayBuffer = (arrayBuffer) => {
  let base64 = '';
  const encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  const bytes = new Uint8Array(arrayBuffer);
  const { byteLength } = bytes;
  const byteRemainder = byteLength % 3;
  const mainLength = byteLength - byteRemainder;

  let a;
  let b;
  let c;
  let d;
  let chunk;

  // Main loop deals with bytes in chunks of 3
  for (let i = 0; i < mainLength; i += 3) {
    // Combine the three bytes into a single integer
    chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

    // Use bitmasks to extract 6-bit segments from the triplet
    a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
    b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
    c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
    d = chunk & 63; // 63       = 2^6 - 1

    // Convert the raw binary segments to the appropriate ASCII encoding
    base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
  }

  // Deal with the remaining bytes and padding
  if (byteRemainder === 1) {
    chunk = bytes[mainLength];

    a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

    // Set the 4 least significant bits to zero
    b = (chunk & 3) << 4; // 3   = 2^2 - 1

    base64 += `${encodings[a]}${encodings[b]}==`;
  } else if (byteRemainder === 2) {
    chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];

    a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
    b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

    // Set the 2 least significant bits to zero
    c = (chunk & 15) << 2; // 15    = 2^4 - 1

    base64 += `${encodings[a]}${encodings[b]}${encodings[c]}=`;
  }

  return base64;
};

export const userInitials = (user) => {
  let initials = '';

  if (user.first_name && user.last_name) {
    initials = user.first_name.charAt(0) + user.last_name.charAt(0);
  } else if (user.first_name) {
    initials = user.first_name.charAt(0);
  } else if (user.last_name) {
    initials = user.last_name.charAt(0);
  } else {
    initials = user?.email?.charAt(0) || 'U';
  }
  return initials.toUpperCase();
};

export const userNameForComment = (user) => {
  let userName = '';

  if (user?.first_name || user?.last_name) {
    userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  } else {
    userName += user?.email || 'User';
  }
  return `${userName} `;
};

export const showCustomLogo = (scribeFile) => {
  return scribeFile?.organization_owner?.logo?.download_url && !scribeFile?.organization_owner?.super_organization?.show_scribe_marketing;
};

export const getRecordAfterId = (visibleActions, recordBeforeActionId) => {
  if (recordBeforeActionId === null) {
    return visibleActions.length === 0 ? null : visibleActions[visibleActions.length - 1].id;
  }

  const currentActionIndex = visibleActions.findIndex((action) => action.id === recordBeforeActionId);

  return currentActionIndex > 0 ? visibleActions[currentActionIndex - 1].id : null;
};

export const getUsernameFromUser = (user) => {
  let username = '';

  if (!user) return username;

  if (user.first_name) {
    username += user.first_name;
  }
  if (user.last_name) {
    if (user.first_name) {
      username += ` ${user.last_name}`;
    } else {
      username += user.last_name;
    }
  }
  if (!user.first_name && !user.last_name) {
    username = user.email;
  }
  return username;
};

/*
    Method that encapsulates all the checks we have to do before firing the
    trackExperiment call in order to have consistent experiment data for authenticated users.
    Parameters:
     - flag, identity --> can be selected from the flagsmith slice
     - loadingState --> can be obtained via the useFlagsmithLoading hook
*/

export const shouldTrackExperiment = (flag, identity, loadingState = {}) => {
  // We want to fire the event only if the flag is enabled
  const isFlagEnabled = flag !== undefined && flag !== '';

  // We want to fire the tracking only if the flags were successfully fetched
  const isFlagsmithLoaded = loadingState && Object.keys(loadingState).length > 0 && !loadingState.isFetching && !loadingState.isLoading;

  // We want to fire the event only if the flagsmith identity is an email not an anonymous uuid
  const isIdentityLoaded = isEmailValid(identity);

  return isFlagEnabled && isFlagsmithLoaded && isIdentityLoaded;
};

export const mergeObjectsInUnique = (array, property) => {
  const newArray = new Map();

  array.forEach((item) => {
    const propertyValue = item[property];

    newArray.has(propertyValue)
      ? newArray.set(propertyValue, { ...item, ...newArray.get(propertyValue) })
      : newArray.set(propertyValue, item);
  });

  return Array.from(newArray.values());
};
